import { useState } from "react";
import { toast } from "sonner";
import { PencilSimple } from "@phosphor-icons/react";
import { mask } from '../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "var(--pr-bg)", card: "var(--pr-card)", ink: "var(--pr-ink)", ink2: "var(--pr-ink2)",
  muted: "var(--pr-muted)", line: "var(--pr-line)", gold: "var(--pr-gold)",
  green: "var(--pr-green)", red: "var(--pr-red)", teal: "var(--pr-teal)",
};

const fmt = (n) => mask(`$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const dayName = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short" });
const dayNum = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const OFF_LABEL = { day_off: "Off day", sick: "Sick day", pto: "PTO day" };
const OFF_EMOJI = { day_off: "😌", sick: "🤒", pto: "🌴" };

export const WeekPlanCard = ({ plan, weeklyTarget, today, onLogDay, onDeleteDay, onChanged,
                              bare = false, title = "This week", showDailyTarget = true,
                            }) => {
  const [openDay, setOpenDay] = useState(null);
  const [openParts, setOpenParts] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [entryAmt, setEntryAmt] = useState("");
  const [busy, setBusy] = useState(false);
  if (!plan) return null;
  const hasTarget = weeklyTarget != null && weeklyTarget > 0;

  const saveEntry = async (entryId) => {
    if (busy) return;
    const amt = Number(entryAmt);
    if (!(amt >= 0)) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/day-entry/${entryId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: amt }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(typeof data.detail === "string" ? data.detail : "Couldn't save that entry"); return; }
      toast.success(amt === 0 ? "Entry removed ✓" : "Entry updated ✓");
      setEditEntry(null); setEntryAmt("");
      onChanged?.();
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  const deleteEntry = async (entryId, i) => {
    if (busy) return;
    if (!window.confirm(`Delete entry ${i + 1}? It's removed from this day's total.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/day-entry/${entryId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) { toast.error(typeof data.detail === "string" ? data.detail : "Couldn't delete that entry"); return; }
      toast.success("Entry removed ✓");
      setEditEntry(null); setEntryAmt("");
      onChanged?.();
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  const markDay = async (date, type) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/day-type`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date, type }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(typeof data.detail === "string" ? data.detail : "Couldn't tag that day"); return; }
      toast.success(type === "none" ? "Back to a work day ✓" : `${OFF_LABEL[type]} saved, targets rebalanced ✓`);
      setOpenDay(null);
      onChanged?.();
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={bare ? "pt-5 mt-5" : "rounded-2xl p-5 lg:p-6 pr-card"}
      style={bare ? { borderTop: `1px solid ${COLOR.line}` } : undefined}
      data-testid="caddy-entries"
    >
      <div className="flex items-baseline justify-between mb-1">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: COLOR.muted }}>{title}</p>
        {hasTarget && (
          <p className="text-[10px]" style={{ color: COLOR.muted }} data-testid="week-plan-days-note">
            {plan.remaining_days} work day{plan.remaining_days === 1 ? "" : "s"} left of {plan.planned_days}
          </p>
        )}
      </div>

      {/* Today's dynamic target, the sheet's shrinking number */}
      {hasTarget && showDailyTarget && (
        <div className="rounded-xl px-4 py-3 mb-3 text-center" style={{ background: "var(--pr-bg)" }} data-testid="week-plan-daily-target">
          {plan.cleared ? (
            <p className="text-sm font-bold" style={{ color: COLOR.green }}>
              Week cleared 🏁 <span className="font-normal" style={{ color: COLOR.ink2 }}>, today's target is</span> $0.00
            </p>
          ) : (
            <>
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: COLOR.muted }}>What do I need today?</p>
              <p className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }} data-testid="week-plan-daily-amount">
                {fmt(plan.daily_target ?? 0)}
              </p>
              <p className="text-[10px]" style={{ color: COLOR.muted }}>
                per remaining work day{plan.initial_daily_target != null && plan.daily_target !== plan.initial_daily_target
                  ? ` · started at ${fmt(plan.initial_daily_target)}` : ""}
              </p>
            </>
          )}
        </div>
      )}

      <div className="space-y-1">
        {plan.days.map((d) => {
          const isOff = d.day_type && !d.worked;
          const isFuture = d.date > today;
          const isToday = d.date === today;
          const showTarget = hasTarget && !plan.cleared && d.in_race && !isOff && !d.worked && (isToday || isFuture);
          return (
            <div key={d.date}>
              <button
                type="button"
                onClick={() => d.in_race && setOpenDay(openDay === d.date ? null : d.date)}
                className="w-full flex items-center justify-between text-sm py-1.5 px-2 rounded-lg text-left"
                style={{
                  background: isToday ? "var(--pr-tint-gold)" : "transparent",
                  border: isToday ? `1px solid ${COLOR.gold}` : "1px solid transparent",
                  opacity: isOff || !d.in_race ? 0.55 : 1,
                }}
                data-testid={`caddy-entry-${d.date}`}
              >
                <span style={{ color: isToday ? COLOR.ink : COLOR.ink2, fontWeight: isToday ? 700 : 400 }}>
                  {dayName(d.date)} <span className="text-xs" style={{ color: COLOR.muted }}>{dayNum(d.date)}</span>
                  {isToday ? " · today" : ""}
                  {!d.in_race && (
                    <span className="ml-1.5 text-[10px]" style={{ color: COLOR.muted }}>outside race</span>
                  )}
                </span>
                <span className="text-right tabular-nums" style={{ color: d.amount ? COLOR.green : d.amount_covered ? COLOR.teal : COLOR.muted, fontWeight: d.amount || d.amount_covered ? 700 : 400 }}>
                  {d.amount ? fmt(d.amount)
                    : d.amount_covered ? `${fmt(d.amount_covered)} PTO`
                    : isOff ? <span data-testid={`caddy-daytype-${d.date}`}>{OFF_EMOJI[d.day_type]} {OFF_LABEL[d.day_type]}</span>
                    : showTarget ? <span className="text-xs" data-testid={`caddy-day-target-${d.date}`}>target {fmt(plan.daily_target ?? 0)}</span>
                    : <span className="text-xs font-normal" style={{ color: COLOR.muted }}>not logged</span>}
                </span>
              </button>

              {d.parts?.length > 1 && (
                <div className="flex justify-end px-2">
                  <button
                    type="button"
                    onClick={() => setOpenParts(openParts === d.date ? null : d.date)}
                    className="text-[10px] font-semibold underline"
                    style={{ color: COLOR.muted }}
                    aria-expanded={openParts === d.date}
                    data-testid={`caddy-day-parts-toggle-${d.date}`}
                  >
                    {d.parts.length} entries {openParts === d.date ? "▲" : "▾"}
                  </button>
                </div>
              )}

              {openParts === d.date && d.parts?.length > 1 && (
                <div className="px-3 py-2.5 mt-1 rounded-lg space-y-1.5" style={{ background: "var(--pr-bg)" }} data-testid={`caddy-day-parts-${d.date}`}>
                  {d.parts.map((p, i) => (
                    <div key={p.entry_id || i} data-testid={`caddy-day-part-${d.date}-${i}`}>
                      {editEntry === p.entry_id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs flex-shrink-0" style={{ color: COLOR.ink2 }}>Entry {i + 1}</span>
                          <div className="relative flex-1">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold" style={{ color: COLOR.muted }}>$</span>
                            <input
                              type="number" inputMode="decimal" step="0.01" min="0" autoFocus
                              value={entryAmt}
                              onChange={(e) => setEntryAmt(e.target.value)}
                              className="w-full pl-6 pr-2 py-1.5 rounded-lg border text-xs font-semibold"
                              style={{ borderColor: COLOR.line, color: COLOR.ink, background: "var(--pr-card)" }}
                              data-testid={`caddy-day-part-input-${d.date}-${i}`}
                            />
                          </div>
                          <button type="button" disabled={busy} onClick={() => saveEntry(p.entry_id)}
                            className="text-[11px] font-bold px-2.5 py-1.5 rounded-full disabled:opacity-40"
                            style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                            data-testid={`caddy-day-part-save-${d.date}-${i}`}>Save</button>
                          <button type="button" disabled={busy} onClick={() => deleteEntry(p.entry_id, i)}
                            className="text-[11px] font-bold px-2 py-1.5 rounded-full border disabled:opacity-40"
                            style={{ borderColor: COLOR.line, color: COLOR.red, background: "var(--pr-card)" }}
                            data-testid={`caddy-day-part-delete-${d.date}-${i}`}>Delete</button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between text-xs">
                          <span style={{ color: COLOR.ink2 }}>Entry {i + 1}</span>
                          <span className="inline-flex items-center gap-2">
                            <span className="font-semibold tabular-nums" style={{ color: COLOR.ink }}>{fmt(p.amount)}</span>
                            <button type="button"
                              onClick={() => { setEditEntry(p.entry_id); setEntryAmt(String(p.amount)); }}
                              className="p-0.5 rounded hover:bg-black/[0.04] transition-colors"
                              style={{ color: COLOR.muted }}
                              aria-label={`Edit entry ${i + 1}`}
                              data-testid={`caddy-day-part-edit-${d.date}-${i}`}>
                              <PencilSimple size={13} />
                            </button>
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {openDay === d.date && (
                <div className="flex flex-wrap gap-1.5 px-2 py-2" data-testid={`caddy-day-actions-${d.date}`}>
                  {!isFuture && (
                    <button
                      type="button"
                      onClick={() => { setOpenDay(null); onLogDay?.(d.date); }}
                      className="text-[11px] font-bold px-3 py-1.5 rounded-full"
                      style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}
                      data-testid={`day-action-log-${d.date}`}
                    >
                      {d.amount ? "$ Edit amount" : "$ Log earnings"}
                    </button>
                  )}
                  {d.amount > 0 && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={async () => {
                        if (!window.confirm(`Delete the ${fmt(d.amount)} logged on ${dayName(d.date)} ${dayNum(d.date)}? This clears the day back to $0.`)) return;
                        setOpenDay(null);
                        await onDeleteDay?.(d.date);
                      }}
                      className="text-[11px] font-bold px-3 py-1.5 rounded-full border disabled:opacity-40"
                      style={{ borderColor: COLOR.line, color: COLOR.red, background: "var(--pr-card)" }}
                      data-testid={`day-action-delete-${d.date}`}
                    >
                      Delete entry
                    </button>
                  )}
                  {["day_off", "pto", "sick"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      disabled={busy || d.day_type === t}
                      onClick={() => markDay(d.date, t)}
                      className="text-[11px] px-3 py-1.5 rounded-full border disabled:opacity-40"
                      style={{ borderColor: COLOR.line, color: COLOR.ink2, background: "var(--pr-card)" }}
                      data-testid={`day-action-${t}-${d.date}`}
                    >
                      {OFF_EMOJI[t]} {OFF_LABEL[t]}
                    </button>
                  ))}
                  {d.day_type && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => markDay(d.date, "none")}
                      className="text-[11px] px-3 py-1.5 rounded-full underline"
                      style={{ color: COLOR.muted }}
                      data-testid={`day-action-clear-${d.date}`}
                    >
                      Clear tag
                    </button>
                  )}
                  {(d.day_type === "pto" || d.day_type === "sick") && (
                    <a href="/app/timeoff" className="text-[11px] px-2 py-1.5 underline" style={{ color: COLOR.teal }}>
                      Cover from PTO bank →
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {hasTarget && plan.regular_off_used > 0 && plan.extra_off_days === 0 && (
        <p className="text-[10px] mt-2 leading-relaxed" style={{ color: COLOR.muted }} data-testid="week-plan-off-note">
          {plan.regular_off_used} regular off day{plan.regular_off_used === 1 ? "" : "s"} used
          {plan.regular_off_remaining > 0 ? ` · ${plan.regular_off_remaining} remaining` : ""}, built into your {plan.planned_days}-workday plan, so your daily target holds steady.
        </p>
      )}
      {hasTarget && plan.extra_off_days > 0 && (
        <p className="text-[10px] mt-2 leading-relaxed" style={{ color: COLOR.muted }} data-testid="week-plan-off-note">
          {plan.extra_off_days} extra day{plan.extra_off_days === 1 ? "" : "s"} off beyond your {plan.planned_off_allowance}-day allowance. Your daily target spreads across the remaining planned workdays, exactly like your sheet.
        </p>
      )}
      {hasTarget && plan.covered_off_days > 0 && (
        <p className="text-[10px] mt-1 leading-relaxed" style={{ color: COLOR.muted }} data-testid="week-plan-covered-note">
          {plan.covered_off_days} paid day{plan.covered_off_days === 1 ? "" : "s"} covered from your PTO bank, daily target unchanged.
        </p>
      )}
    </div>
  );
};
